/**
 * Page Corbeille RH - ÉTAPE H
 * Consultation et restauration des documents/contrats/fiches de paie supprimés
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { 
  Trash2, RotateCcw, FileText, FileSignature, Receipt, Search,
  Loader2, AlertCircle, AlertTriangle, Clock, User, Calendar
} from "lucide-react";
import { formatDateFr } from "../../components/ui/date-picker-fr";
import { 
  getDocumentCategoryLabel, 
  getContractDocTypeLabel 
} from "../../lib/hr-constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Calculate days until permanent deletion
const getDaysUntilDeletion = (deletedAt) => {
  if (!deletedAt) return 30;
  const deleteDate = new Date(deletedAt);
  const permanentDeleteDate = new Date(deleteDate);
  permanentDeleteDate.setDate(permanentDeleteDate.getDate() + 30);
  const today = new Date();
  const daysLeft = Math.ceil((permanentDeleteDate - today) / (1000 * 60 * 60 * 24));
  return Math.max(0, daysLeft);
};

// Deleted item card
const DeletedItemCard = ({ item, type, onRestore, onPermanentDelete }) => {
  const daysLeft = getDaysUntilDeletion(item.deleted_at);
  const isUrgent = daysLeft <= 7;
  
  // Icon based on type
  const getIcon = () => {
    switch (type) {
      case "document": return FileText;
      case "contract": return FileSignature;
      case "payslip": return Receipt;
      default: return FileText;
    }
  };
  const Icon = getIcon();
  
  // Get display name
  const getDisplayName = () => {
    switch (type) {
      case "document":
        return item.custom_label || item.original_filename;
      case "contract":
        return item.subject;
      case "payslip":
        return `Fiche de paie - ${item.month}/${item.year}`;
      default:
        return item.original_filename;
    }
  };
  
  // Get badge info
  const getBadgeInfo = () => {
    switch (type) {
      case "document":
        return { label: getDocumentCategoryLabel(item.category), color: "bg-blue-100 text-blue-700" };
      case "contract":
        return { label: getContractDocTypeLabel(item.type), color: "bg-purple-100 text-purple-700" };
      case "payslip":
        return { label: "Fiche de paie", color: "bg-green-100 text-green-700" };
      default:
        return { label: type, color: "bg-slate-100 text-slate-700" };
    }
  };
  const badgeInfo = getBadgeInfo();
  
  return (
    <div className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
      isUrgent ? 'border-red-200 bg-red-50/30' : 'border-slate-200 hover:bg-slate-50'
    }`}>
      {/* Icon */}
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
        isUrgent ? 'bg-red-100' : 'bg-slate-100'
      }`}>
        <Icon className={`w-5 h-5 ${isUrgent ? 'text-red-500' : 'text-slate-500'}`} />
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <p className="text-sm font-medium text-slate-900 truncate">
            {getDisplayName()}
          </p>
          <Badge variant="outline" className={`${badgeInfo.color} text-xs`}>
            {badgeInfo.label}
          </Badge>
        </div>
        
        <div className="flex items-center gap-3 text-xs text-slate-500">
          {item.employee_name && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {item.employee_name}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Supprimé le {formatDateFr(item.deleted_at)}
          </span>
        </div>
      </div>
      
      {/* Days until deletion */}
      <div className={`text-center px-3 py-1 rounded-full text-xs font-medium ${
        isUrgent 
          ? 'bg-red-100 text-red-700' 
          : 'bg-amber-100 text-amber-700'
      }`}>
        <Clock className="w-3 h-3 inline mr-1" />
        {daysLeft}j restants
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => onRestore(item, type)}
          className="text-green-600 border-green-200 hover:bg-green-50"
        >
          <RotateCcw className="w-4 h-4 mr-1" />
          Restaurer
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => onPermanentDelete(item, type)}
          className="text-red-600 border-red-200 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4 mr-1" />
          Supprimer
        </Button>
      </div>
    </div>
  );
};

// Empty state
const EmptyState = ({ type }) => {
  const getTypeLabel = () => {
    switch (type) {
      case "document": return "documents";
      case "contract": return "contrats";
      case "payslip": return "fiches de paie";
      default: return "éléments";
    }
  };
  
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <Trash2 className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">
        Corbeille vide
      </h3>
      <p className="text-sm text-slate-500 max-w-sm">
        Aucun {getTypeLabel()} supprimé. Les éléments supprimés apparaîtront ici pendant 30 jours avant d'être définitivement supprimés.
      </p>
    </div>
  );
};

export default function TrashPage() {
  const { token } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState("documents");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data
  const [deletedDocuments, setDeletedDocuments] = useState([]);
  const [deletedContracts, setDeletedContracts] = useState([]);
  const [deletedPayslips, setDeletedPayslips] = useState([]);
  
  // Search
  const [searchQuery, setSearchQuery] = useState("");
  
  // Delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Fetch all deleted items
  const fetchDeletedItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Single endpoint returns all trash items grouped by type
      const response = await axios.get(`${API}/hr/trash`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const items = response.data.items || [];
      
      // Separate by type
      setDeletedDocuments(items.filter(i => i.resource_type === "document"));
      setDeletedContracts(items.filter(i => i.resource_type === "contract"));
      setDeletedPayslips(items.filter(i => i.resource_type === "payslip"));
      
    } catch (err) {
      console.error("Error fetching deleted items:", err);
      setError("Impossible de charger les éléments supprimés");
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    fetchDeletedItems();
  }, [fetchDeletedItems]);
  
  // Filter items by search
  const filteredDocuments = useMemo(() => {
    if (!searchQuery) return deletedDocuments;
    const q = searchQuery.toLowerCase();
    return deletedDocuments.filter(d => 
      d.original_filename?.toLowerCase().includes(q) ||
      d.custom_label?.toLowerCase().includes(q) ||
      d.employee_name?.toLowerCase().includes(q)
    );
  }, [deletedDocuments, searchQuery]);
  
  const filteredContracts = useMemo(() => {
    if (!searchQuery) return deletedContracts;
    const q = searchQuery.toLowerCase();
    return deletedContracts.filter(c => 
      c.subject?.toLowerCase().includes(q) ||
      c.employee_name?.toLowerCase().includes(q)
    );
  }, [deletedContracts, searchQuery]);
  
  const filteredPayslips = useMemo(() => {
    if (!searchQuery) return deletedPayslips;
    const q = searchQuery.toLowerCase();
    return deletedPayslips.filter(p => 
      p.employee_name?.toLowerCase().includes(q) ||
      String(p.year).includes(q)
    );
  }, [deletedPayslips, searchQuery]);
  
  // Counts
  const totalCount = deletedDocuments.length + deletedContracts.length + deletedPayslips.length;
  
  // Restore item
  const handleRestore = async (item, type) => {
    try {
      let endpoint;
      switch (type) {
        case "document":
          endpoint = `${API}/hr/documents/${item.id}/restore`;
          break;
        case "contract":
          endpoint = `${API}/hr/contracts/${item.id}/restore`;
          break;
        case "payslip":
          endpoint = `${API}/hr/payslips/${item.id}/restore`;
          break;
        default:
          return;
      }
      
      await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Élément restauré", {
        description: "L'élément a été restauré avec succès"
      });
      
      await fetchDeletedItems();
      
    } catch (err) {
      console.error("Error restoring item:", err);
      toast.error("Erreur", { description: "Impossible de restaurer l'élément" });
    }
  };
  
  // Permanent delete
  const handlePermanentDeleteClick = (item, type) => {
    setConfirmDelete({ item, type });
  };
  
  const confirmPermanentDelete = async () => {
    if (!confirmDelete) return;
    
    setIsDeleting(true);
    try {
      let endpoint;
      switch (confirmDelete.type) {
        case "document":
          endpoint = `${API}/hr/documents/${confirmDelete.item.id}/permanent`;
          break;
        case "contract":
          endpoint = `${API}/hr/contracts/${confirmDelete.item.id}/permanent`;
          break;
        case "payslip":
          endpoint = `${API}/hr/payslips/${confirmDelete.item.id}/permanent`;
          break;
        default:
          return;
      }
      
      await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Supprimé définitivement", {
        description: "L'élément a été supprimé définitivement"
      });
      
      setConfirmDelete(null);
      await fetchDeletedItems();
      
    } catch (err) {
      console.error("Error permanently deleting item:", err);
      toast.error("Erreur", { description: "Impossible de supprimer définitivement" });
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Render loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  // Render error
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <AlertCircle className="w-12 h-12 mb-2" />
        <p>{error}</p>
        <Button variant="outline" onClick={fetchDeletedItems} className="mt-4">
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Trash2 className="w-7 h-7 text-slate-600" />
            Corbeille
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {totalCount} élément(s) • Les éléments sont supprimés définitivement après 30 jours
          </p>
        </div>
      </div>
      
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher dans la corbeille..."
          className="pl-9"
        />
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Documents ({deletedDocuments.length})
          </TabsTrigger>
          <TabsTrigger value="contracts" className="flex items-center gap-2">
            <FileSignature className="w-4 h-4" />
            Contrats ({deletedContracts.length})
          </TabsTrigger>
          <TabsTrigger value="payslips" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Paie ({deletedPayslips.length})
          </TabsTrigger>
        </TabsList>
        
        {/* Documents */}
        <TabsContent value="documents" className="mt-4">
          {filteredDocuments.length === 0 ? (
            <EmptyState type="document" />
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <ScrollArea className="max-h-[500px]">
                  <div className="space-y-3">
                    {filteredDocuments.map(doc => (
                      <DeletedItemCard
                        key={doc.id}
                        item={doc}
                        type="document"
                        onRestore={handleRestore}
                        onPermanentDelete={handlePermanentDeleteClick}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Contracts */}
        <TabsContent value="contracts" className="mt-4">
          {filteredContracts.length === 0 ? (
            <EmptyState type="contract" />
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <ScrollArea className="max-h-[500px]">
                  <div className="space-y-3">
                    {filteredContracts.map(contract => (
                      <DeletedItemCard
                        key={contract.id}
                        item={contract}
                        type="contract"
                        onRestore={handleRestore}
                        onPermanentDelete={handlePermanentDeleteClick}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Payslips */}
        <TabsContent value="payslips" className="mt-4">
          {filteredPayslips.length === 0 ? (
            <EmptyState type="payslip" />
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <ScrollArea className="max-h-[500px]">
                  <div className="space-y-3">
                    {filteredPayslips.map(payslip => (
                      <DeletedItemCard
                        key={payslip.id}
                        item={payslip}
                        type="payslip"
                        onRestore={handleRestore}
                        onPermanentDelete={handlePermanentDeleteClick}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Permanent delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">
              Suppression définitive
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Êtes-vous sûr de vouloir supprimer définitivement cet élément ?
              </p>
              <p className="text-red-600 flex items-center gap-2 font-medium">
                <AlertTriangle className="w-4 h-4" />
                Cette action est irréversible.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmPermanentDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer définitivement
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
